import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useAuth } from "../lib/auth-context.js";
import { ApiError } from "../lib/api.js";
import {
  deleteEquityGrant,
  listEquityGrants,
} from "../lib/api-modules/equity-grants.js";
import {
  EQUITY_GRANT_TYPE_LABELS,
  type EquityGrant,
  type MembershipRole,
} from "../lib/api-modules/types.js";
import { formatCentsCurrency } from "../lib/util/money.js";
import {
  projectVestTaxEvents,
  vestingCalendar,
} from "../lib/util/equity.js";
import { ErrorMessage } from "../components/auth/error-message.js";
import { ConfirmButton } from "../components/confirm-button.js";
import { Toast, useToast } from "../components/toast.js";
import { NotesPanel } from "../components/notes-panel.js";
import { DecisionsPanel } from "../components/decisions-panel.js";
import { EquityGrantForm } from "../components/equity/EquityGrantForm.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

const READ_ONLY_ROLES: ReadonlySet<MembershipRole> = new Set([
  "viewer",
  "auditor",
]);

/**
 * /equity-grants — RSU/ISO/NSO/ESPP/RSA tracker.
 *
 * Shows a 24-month forward vesting calendar (summed shares per month
 * across all active grants), back-of-envelope tax projections per
 * grant for that window, and an inline grant list with notes +
 * decisions panels for negotiation / exercise history. Tax projections
 * are NOT authoritative — Phase 7 owns real tax modeling.
 */
export function EquityGrantsPage() {
  const { user } = useAuth();
  if (!user) return null;
  const canWrite = !READ_ONLY_ROLES.has(user.role);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Equity grants
          </h1>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
            Vesting calendar + tax-event projection. Back-of-envelope —
            not authoritative tax math.
          </p>
        </div>
      </header>
      {!canWrite && (
        <p className="mb-6 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
          Read-only access — ask an owner or admin to make changes.
        </p>
      )}
      <EquityGrantsBody canWrite={canWrite} />
      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.equityGrants} />
      </div>
    </div>
  );
}

function EquityGrantsBody({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [expandId, setExpandId] = useState<string | null>(null);

  const grantsQuery = useQuery({
    queryKey: ["equity-grants", showInactive],
    queryFn: () => listEquityGrants(showInactive),
  });

  const archiveMutation = useMutation({
    mutationFn: (grantId: string) => deleteEquityGrant(grantId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["equity-grants"] });
      toast.show("Grant deactivated");
    },
    onError: () => toast.show("Couldn't deactivate"),
  });

  const grants = grantsQuery.data?.equity_grants ?? [];

  // Forward 24-month window for the calendar + projections.
  const { from, to, monthLabels } = useMemo(() => {
    const today = new Date();
    const fromY = today.getUTCFullYear();
    const fromM = today.getUTCMonth();
    const fromD = `${fromY}-${String(fromM + 1).padStart(2, "0")}-01`;
    const toY = today.getUTCFullYear() + 2;
    const toM = today.getUTCMonth();
    const lastDay = new Date(Date.UTC(toY, toM + 1, 0)).getUTCDate();
    const toD = `${toY}-${String(toM + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    const labels: string[] = [];
    for (let i = 0; i < 24; i++) {
      const m = (fromM + i) % 12;
      const y = fromY + Math.floor((fromM + i) / 12);
      labels.push(`${y}-${String(m + 1).padStart(2, "0")}`);
    }
    return { from: fromD, to: toD, monthLabels: labels };
  }, []);

  const calendarData = useMemo(() => {
    const cal = vestingCalendar(grants, from, to);
    return monthLabels.map((ym) => ({
      month: ym.slice(2), // YY-MM compact
      ym,
      shares: cal.get(ym) ?? 0,
    }));
  }, [grants, from, to, monthLabels]);

  const projections = useMemo(
    () => projectVestTaxEvents(grants, from, to),
    [grants, from, to],
  );

  if (grantsQuery.isLoading) {
    return (
      <p className="text-sm text-zinc-500" role="status">
        Loading grants…
      </p>
    );
  }
  if (grantsQuery.error) {
    return (
      <ErrorMessage>
        {grantsQuery.error instanceof ApiError
          ? grantsQuery.error.message
          : "Couldn't load equity grants."}
      </ErrorMessage>
    );
  }

  const activeCount = grants.filter((g) => g.is_active !== false).length;
  const totalShares = grants
    .filter((g) => g.is_active !== false)
    .reduce((s, g) => s + g.total_shares, 0);
  const projTotal = projections.reduce(
    (s, p) =>
      s +
      (p.projected_ordinary_income_cents ?? 0) +
      (p.projected_bargain_element_cents ?? 0),
    0,
  );

  return (
    <section>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Headline label="Active grants" value={activeCount} count />
        <Headline label="Total shares" value={Math.round(totalShares)} count />
        <Headline label="24m projected income (cents)" value={projTotal} />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-400">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive
        </label>
        {canWrite && !showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-md bg-brand-500 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-brand-600"
          >
            Add grant
          </button>
        )}
      </div>

      {showCreate && (
        <div className="mb-4">
          <EquityGrantForm mode="create" onClose={() => setShowCreate(false)} />
        </div>
      )}

      {/* Vesting calendar */}
      {grants.length > 0 && (
        <div className="mb-6 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
          <h2 className="mb-3 text-sm font-semibold">
            Vesting calendar — next 24 months
          </h2>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={calendarData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="month" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip
                  formatter={(v) => [
                    Math.round(Number(v ?? 0)).toLocaleString(),
                    "shares",
                  ]}
                  labelFormatter={(label, payload) => {
                    const p = payload?.[0]?.payload as
                      | { ym: string }
                      | undefined;
                    return p?.ym ?? (typeof label === "string" ? label : "");
                  }}
                />
                <Bar dataKey="shares" fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tax projections */}
      {projections.length > 0 && (
        <div className="mb-6 rounded-md border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-950">
          <h2 className="mb-1 text-sm font-semibold">
            24m tax projections (back-of-envelope)
          </h2>
          <p className="mb-3 text-[11px] text-zinc-500">
            Marginal rates, AMT, and ESPP qualifying-disposition rules
            land in Phase 7. ESPP income shown is the 15% discount
            assumption.
          </p>
          <table className="w-full text-xs">
            <thead className="text-left text-zinc-500">
              <tr>
                <th className="pb-1">Ticker</th>
                <th className="pb-1">Type</th>
                <th className="pb-1 text-right">Shares</th>
                <th className="pb-1 text-right">Ordinary income</th>
                <th className="pb-1 text-right">Bargain element</th>
              </tr>
            </thead>
            <tbody>
              {projections.map((p) => (
                <tr
                  key={p.grant_id}
                  className="border-t border-zinc-100 dark:border-zinc-900"
                >
                  <td className="py-1">{p.ticker}</td>
                  <td className="py-1">{p.grant_type}</td>
                  <td className="py-1 text-right font-mono">
                    {Math.round(p.shares_vesting).toLocaleString()}
                  </td>
                  <td className="py-1 text-right font-mono">
                    {p.projected_ordinary_income_cents !== null
                      ? formatCentsCurrency(p.projected_ordinary_income_cents)
                      : "—"}
                  </td>
                  <td className="py-1 text-right font-mono">
                    {p.projected_bargain_element_cents !== null
                      ? formatCentsCurrency(p.projected_bargain_element_cents)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Grant list */}
      {grants.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
          No equity grants yet. {canWrite ? 'Click "Add grant" to start.' : ""}
        </p>
      ) : (
        <ul className="flex flex-col gap-3">
          {grants.map((g) =>
            editingId === g.grant_id ? (
              <li key={g.grant_id}>
                <EquityGrantForm
                  mode="edit"
                  grant={g}
                  onClose={() => setEditingId(null)}
                />
              </li>
            ) : (
              <GrantCard
                key={g.grant_id}
                grant={g}
                canWrite={canWrite}
                expanded={expandId === g.grant_id}
                onToggle={() =>
                  setExpandId((prev) =>
                    prev === g.grant_id ? null : g.grant_id,
                  )
                }
                onEdit={() => setEditingId(g.grant_id)}
                onArchive={() => archiveMutation.mutate(g.grant_id)}
                isMutating={archiveMutation.isPending}
              />
            ),
          )}
        </ul>
      )}

      <Toast message={toast.message} />
    </section>
  );
}

function Headline({
  label,
  value,
  count,
}: {
  label: string;
  value: number;
  count?: boolean;
}) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <div className="text-xs text-zinc-500">{label}</div>
      <div className="mt-1 text-xl font-semibold">
        {count ? value.toLocaleString() : formatCentsCurrency(value)}
      </div>
    </div>
  );
}

function GrantCard({
  grant,
  canWrite,
  expanded,
  onToggle,
  onEdit,
  onArchive,
  isMutating,
}: {
  grant: EquityGrant;
  canWrite: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onArchive: () => void;
  isMutating: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const inBlackout = grant.blackout_windows.some(
    (w) => today >= w.start && today <= w.end,
  );
  const sharesVested = grant.vest_events
    .filter((v) => v.date <= today)
    .reduce((s, v) => s + v.shares, 0);
  const sharesUpcoming = grant.vest_events
    .filter((v) => v.date > today)
    .reduce((s, v) => s + v.shares, 0);

  return (
    <li className="rounded-md border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 truncate text-base font-medium">
            {grant.ticker} · {EQUITY_GRANT_TYPE_LABELS[grant.grant_type]}
            {!grant.is_active && (
              <span className="rounded-full bg-zinc-200 px-2 py-0.5 text-xs font-normal text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                inactive
              </span>
            )}
            {inBlackout && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-normal text-red-800 dark:bg-red-900/40 dark:text-red-300">
                blackout today
              </span>
            )}
          </h3>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            granted <time dateTime={grant.grant_date}>{grant.grant_date}</time>
            {" · "}
            {grant.total_shares.toLocaleString()} shares
            {grant.strike_price_cents !== undefined && (
              <> · strike {formatCentsCurrency(grant.strike_price_cents)}</>
            )}
            {grant.most_recent_fmv_cents !== undefined && (
              <> · FMV {formatCentsCurrency(grant.most_recent_fmv_cents)}</>
            )}
            {grant.expiration_date && <> · expires {grant.expiration_date}</>}
          </p>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            vested {Math.round(sharesVested).toLocaleString()} ·
            upcoming {Math.round(sharesUpcoming).toLocaleString()}
          </p>
          {grant.notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-700 dark:text-zinc-300">
              {grant.notes}
            </p>
          )}
        </div>
        {canWrite && grant.is_active !== false && (
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={onEdit}
              disabled={isMutating}
              className="rounded-md border border-zinc-300 px-2 py-1 text-xs hover:bg-zinc-50 disabled:opacity-60 dark:border-zinc-700 dark:hover:bg-zinc-900"
            >
              Edit
            </button>
            <ConfirmButton
              label="Deactivate"
              pendingLabel="Deactivating…"
              size="xs"
              ariaLabel={`Deactivate ${grant.ticker} grant`}
              onConfirm={onArchive}
              isPending={isMutating}
            />
          </div>
        )}
      </header>
      <div className="mt-3 border-t border-zinc-200 pt-3 dark:border-zinc-800">
        <button
          type="button"
          onClick={onToggle}
          className="text-xs text-zinc-500 hover:underline"
        >
          {expanded ? "Hide details" : "Show details"}
        </button>
        {expanded && (
          <div className="mt-3 flex flex-col gap-4">
            {grant.vest_events.length > 0 && (
              <div>
                <h4 className="mb-1 text-xs font-semibold">Vest schedule</h4>
                <ul className="max-h-48 overflow-auto text-xs">
                  {grant.vest_events.map((v, i) => (
                    <li
                      key={i}
                      className={`flex items-center justify-between border-b border-zinc-100 py-1 last:border-b-0 dark:border-zinc-900 ${
                        v.date <= today ? "text-zinc-500" : ""
                      }`}
                    >
                      <span>
                        <time dateTime={v.date}>{v.date}</time>
                        {v.is_cliff && (
                          <span className="ml-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                            cliff
                          </span>
                        )}
                        {v.date <= today && (
                          <span className="ml-2 text-[10px]">vested</span>
                        )}
                      </span>
                      <span className="font-mono">
                        {v.shares.toLocaleString(undefined, {
                          maximumFractionDigits: 4,
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {grant.blackout_windows.length > 0 && (
              <div>
                <h4 className="mb-1 text-xs font-semibold">Blackout windows</h4>
                <ul className="text-xs">
                  {grant.blackout_windows.map((w, i) => (
                    <li key={i}>
                      {w.start} → {w.end} · {w.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <NotesPanel
              entityType="equity_grant"
              entityId={grant.grant_id}
            />
            <DecisionsPanel
              entityType="equity_grant"
              entityId={grant.grant_id}
            />
          </div>
        )}
      </div>
    </li>
  );
}
